/**
 * In-app "Begrippen"-knop (ⓘ) — context-afhankelijke woordenlijst van de
 * meta-termen die de UI gebruikt (Concept, stereotype, facet, plateau, …).
 *
 * Single source of truth is `docs/architecture/ui-begrippenlijst.md`, via Vite's
 * `?raw` als string in de bundle geladen (zelfde patroon als HelpDialog). Elk
 * scherm geeft de term-id's mee die daar voorkomen; de popover toont enkel die.
 * Bijwerken van de MD is een vaste taak bij milestone-afronding.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import glossaryMd from "../../../../docs/architecture/ui-begrippenlijst.md?raw";

interface GlossaryTerm {
  id: string;
  nl: string;
  en: string;
  defNl: string;
  defEn: string;
}

/** Parse de MD: strip code-fences (bevatten het voorbeeld-format), split op
 *  `### `-secties en lees de `- key: value`-regels per term. */
function parseGlossary(md: string): Map<string, GlossaryTerm> {
  const map = new Map<string, GlossaryTerm>();
  const withoutFences = md.replace(/```[\s\S]*?```/g, "");
  const blocks = withoutFences.split(/^### /m).slice(1);
  for (const block of blocks) {
    const fields: Record<string, string> = {};
    for (const line of block.split("\n")) {
      const m = line.match(/^- (\w+):\s?(.*)$/);
      if (m?.[1]) fields[m[1]] = (m[2] ?? "").trim();
    }
    const id = fields.id;
    if (!id) continue;
    map.set(id, {
      id,
      nl: fields.nl ?? fields.en ?? id,
      en: fields.en ?? fields.nl ?? id,
      defNl: fields.def_nl ?? fields.def_en ?? "",
      defEn: fields.def_en ?? fields.def_nl ?? "",
    });
  }
  return map;
}

const GLOSSARY = parseGlossary(glossaryMd);

/**
 * ⓘ-knop die een popover opent met de doorgegeven termen + hun betekenis in de
 * UI-taal. Onbekende id's worden stil overgeslagen (dev-warn) zodat een typo in
 * een scherm-lijst niet crasht.
 */
export function GlossaryInfoButton({
  termIds,
  className,
}: {
  termIds: string[];
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const terms = termIds
    .map((id) => {
      const term = GLOSSARY.get(id);
      if (!term && import.meta.env.DEV) console.warn(`[uiGlossary] onbekende term-id: ${id}`);
      return term;
    })
    .filter((term): term is GlossaryTerm => !!term);

  if (terms.length === 0) return null;

  return (
    <div className={`relative inline-block ${className ?? ""}`} ref={ref}>
      <button
        type="button"
        className="flex h-5 w-5 items-center justify-center rounded-full border border-[var(--ld-muted,#94a3b8)] font-semibold text-[11px] text-[var(--ld-text,#475569)] hover:border-[var(--ld-accent,#0284c7)] hover:text-[var(--ld-accent,#0284c7)]"
        onClick={() => setOpen((v) => !v)}
        title={t("uiGlossary.button")}
        aria-label={t("uiGlossary.button")}
        aria-expanded={open}
      >
        i
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 max-h-[60vh] w-80 overflow-auto rounded-lg border border-[var(--ld-border,#cbd5e1)] bg-[var(--ld-surface,#fff)] p-3 text-left shadow-xl">
          <h4 className="mb-2 font-semibold text-[var(--ld-muted,#64748b)] text-xs">
            {t("uiGlossary.title")}
          </h4>
          <dl className="flex flex-col gap-2">
            {terms.map((term) => (
              <div key={term.id}>
                <dt className="font-semibold text-[12px] text-[var(--ld-text,#0f172a)]">
                  {lang === "en" ? term.en : term.nl}
                </dt>
                <dd className="text-[11px] text-[var(--ld-muted,#475569)] leading-snug">
                  {lang === "en" ? term.defEn : term.defNl}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
